<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import V2ReservationTablePicker from '@/components/v2/reservations/V2ReservationTablePicker.vue';
  import type { IReservationTablePickerValidity } from '@/interfaces/IReservationUi';
  import { useModal } from '@/composition/use-modal';
  import {
    formatTime,
    getReservationTimeZone,
  } from '@/domain/reservations/reservation-time';
  import type { IReservation } from '@/interfaces/IReservation';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  const props = defineProps<{
    reservation: Pick<
      IReservation,
      'id' | 'partySize' | 'startsAt' | 'durationMin' | 'tableIds'
    >;
    mode?: 'reservation' | 'now';
  }>();
  const modals = useModal();
  const restaurant = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;
  const selection = ref([...props.reservation.tableIds]);
  const validity = ref<IReservationTablePickerValidity>({
    loading: true,
    valid: false,
    force: false,
  });
  const time = computed(() =>
    formatTime(
      props.reservation.startsAt,
      getReservationTimeZone(
        restaurant.selectedBranch?.location,
        restaurant.restaurant?.country
      )
    )
  );
  const people = computed(() =>
    String(
      proxy.$tc('reservations.common.people', props.reservation.partySize, {
        count: props.reservation.partySize,
      })
    )
  );
  function submit() {
    if (!validity.value.valid) return;
    void modals.dialogSubmit('v2-reservation-tables', {
      tableIds: selection.value,
      force: validity.value.force,
    });
  }
</script>

<template>
  <div class="reservation-table-modal">
    <header>
      <h2>{{ $t('reservations.tablePicker.title') }}</h2>
      <p>{{ $t('reservations.tablePicker.subtitle', { people, time }) }}</p>
    </header>
    <div class="modal-content">
      <V2ReservationTablePicker
        v-model="selection"
        :party-size="reservation.partySize"
        :starts-at="reservation.startsAt"
        :duration-min="reservation.durationMin"
        :exclude-reservation-id="reservation.id"
        :mode="mode"
        @validity="validity = $event"
      />
    </div>
    <footer>
      <PosUiButton
        color="neutral"
        variant="outline"
        @click="modals.hide('v2-reservation-tables')"
        >{{ $t('cancel') }}</PosUiButton
      ><PosUiButton :disabled="!validity.valid" @click="submit">{{
        $tc('reservations.tablePicker.confirm', selection.length)
      }}</PosUiButton>
    </footer>
  </div>
</template>

<style scoped lang="scss">
  .reservation-table-modal {
    @apply tw-flex tw-max-h-[85vh] tw-flex-col tw-bg-white;
    header {
      @apply tw-border-b tw-border-neutral-200 tw-p-5;
      h2 {
        @apply tw-text-lg tw-font-semibold tw-text-neutral-900;
      }
      p {
        @apply tw-mt-1 tw-text-sm tw-text-neutral-500;
      }
    }
    .modal-content {
      @apply tw-min-h-0 tw-overflow-y-auto tw-p-5;
    }
    footer {
      @apply tw-flex tw-flex-shrink-0 tw-justify-end tw-gap-2 tw-border-t tw-border-neutral-200 tw-p-4;
    }
  }
</style>
